import * as T from 'three';

/** Hold the impact viewpoint, then turn in place toward the falling body. */
export class DeathPresentation {
  readonly holdSeconds = .24;
  private running = false;
  private age = 0;
  private readonly heldPosition = new T.Vector3();
  private readonly heldRotation = new T.Quaternion();
  private readonly heldUp = new T.Vector3();
  private heldFov = 65;
  private readonly look = new T.Matrix4();
  private readonly targetRotation = new T.Quaternion();
  private readonly trackingRotation = new T.Quaternion();

  get active(): boolean { return this.running; }
  get elapsed(): number { return this.age; }
  get flashAlpha(): number {
    if (!this.running) return 0;
    const attack = T.MathUtils.smoothstep(this.age, 0, .045);
    const decay = 1 - T.MathUtils.smoothstep(this.age, .045, .55);
    return .32 * attack * decay;
  }

  /** `renderOrigin` belongs to the captured camera's last rendered frame. */
  start(camera: T.PerspectiveCamera, _torso: T.Vector3, renderOrigin?: T.Vector3): void {
    // Repeated authoritative updates cannot restart the local impact.
    if (this.running) return;
    this.running = true;
    this.age = 0;
    this.heldPosition.copy(camera.position);
    if (renderOrigin) this.heldPosition.add(renderOrigin);
    this.heldRotation.copy(camera.quaternion);
    this.trackingRotation.copy(camera.quaternion);
    this.heldUp.copy(camera.up);
    this.heldFov = camera.fov;
  }

  reset(): void { this.running = false; this.age = 0; }

  /** Apply after the ordinary camera rig. The torso is in current render space. */
  update(camera: T.PerspectiveCamera, torso: T.Vector3, dt: number, renderOrigin?: T.Vector3): void {
    if (!this.running) return;
    const elapsed = Math.max(0, dt);
    this.age += elapsed;
    camera.position.copy(this.heldPosition);
    if (renderOrigin) camera.position.sub(renderOrigin);
    if (camera.fov !== this.heldFov) {
      camera.fov = this.heldFov;
      camera.updateProjectionMatrix();
    }
    camera.up.copy(this.heldUp);
    if (this.age <= this.holdSeconds) {
      camera.quaternion.copy(this.heldRotation);
      return;
    }
    const following = this.age - this.holdSeconds;
    const step = Math.min(elapsed, following);
    const ease = T.MathUtils.smoothstep(following, 0, .3);
    this.look.lookAt(camera.position, torso, camera.up);
    this.targetRotation.setFromRotationMatrix(this.look);
    // The gameplay camera may have written its own pose this frame. Keep the
    // death rotation's history separate so the follow remains continuous.
    this.trackingRotation.slerp(this.targetRotation, 1 - Math.exp(-9 * step * ease));
    camera.quaternion.copy(this.trackingRotation);
  }
}
